import { useState, useEffect } from "react";
import { BarChart3, TrendingUp, Calendar, Zap, Shield, ArrowUp, ArrowDown, Minus, ChevronDown, ChevronUp } from "lucide-react";
import { useCostAnalysis } from "../../hooks/aiHooks/useCostAnalysis";
import AIThinkingLoader from "../common/AIThinkingLoader";

const AICostAnalysisCard = () => {
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const { getLatestCostAnalysis } = useCostAnalysis();

  useEffect(() => {
    fetchAnalysisData();
  }, []);

  const fetchAnalysisData = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await getLatestCostAnalysis();
      if (result.success && result.data) {
        setAnalysis(result.data);
      }
    } catch (err) {
      console.error("Error fetching cost analysis:", err);
      setError("Failed to load AI analysis data");
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return "N/A";
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const formatPercent = (value) => {
    if (!value && value !== 0) return "N/A";
    return `${value > 0 ? "+" : ""}${value.toFixed(1)}%`;
  };

  const getTrendIcon = (change) => {
    if (change > 0) return <ArrowUp className="w-4 h-4 text-red-400" />;
    if (change < 0) return <ArrowDown className="w-4 h-4 text-green-400" />;
    return <Minus className="w-4 h-4 text-blue-400" />;
  };

  const getDirectionColor = (direction) => {
    switch (direction?.toUpperCase()) {
      case "INCREASE":
        return "text-red-400";
      case "DECREASE":
        return "text-green-400";
      default:
        return "text-blue-400";
    }
  };

  const getRecommendationActionColor = (action) => {
    switch (action?.toUpperCase()) {
      case "OPTIMIZE_VMS":
        return "bg-blue-500/20 text-blue-400 border-blue-500/30";
      case "OPTIMIZE_STORAGE":
        return "bg-cyan-500/20 text-cyan-400 border-cyan-500/30";
      case "CONSIDER_RESERVATIONS":
        return "bg-purple-500/20 text-purple-400 border-purple-500/30";
      case "INVESTIGATE":
        return "bg-orange-500/20 text-orange-400 border-orange-500/30";
      default:
        return "bg-gray-500/20 text-gray-400 border-gray-500/30";
    }
  };

  const getConfidenceColor = (confidence) => {
    switch (confidence?.toUpperCase()) {
      case "HIGH":
        return "bg-green-500/20 text-green-400 border-green-500/30";
      case "MEDIUM":
        return "bg-yellow-500/20 text-yellow-400 border-yellow-500/30";
      default:
        return "bg-gray-500/20 text-gray-400 border-gray-500/30";
    }
  };

  const toggleExpand = () => setIsExpanded(!isExpanded);

  if (loading) {
    return <AIThinkingLoader />;
  }

  if (error) {
    return null;
  }

  if (!analysis) {
    return null;
  }

  return (
    <div className="rounded-xl border border-primary-cyan/20 overflow-hidden mx-2 sm:mx-4 md:mx-8 mb-2">
      <div className="px-3 sm:px-5 py-3 border-b border-primary-cyan/20 sticky z-20" style={{ backgroundColor: "#10182b" }}>
        <div className="flex flex-wrap justify-between items-center gap-2">
          <div className="flex flex-wrap items-center gap-2">
            <div className="w-7 h-7 rounded-lg bg-primary-cyan/20 flex items-center justify-center">
              <BarChart3 className="w-4 h-4 text-primary-cyan" />
            </div>
            <span className="text-primary-cyan text-sm sm:text-base font-bold tracking-wide">AI COST INSIGHTS</span>
            <span className="text-[10px] sm:text-[11px] text-gray-500 uppercase tracking-wider font-medium">Last 12 Months Analysis</span>
          </div>
          <button
            onClick={toggleExpand}
            className="flex items-center gap-1 px-2 sm:px-3 py-1 rounded-lg bg-primary-cyan/10 hover:bg-primary-cyan/20 transition-all duration-200 text-primary-cyan text-xs sm:text-sm font-medium"
          >
            {isExpanded ? (
              <>
                <ChevronUp className="w-3 h-3 sm:w-4 sm:h-4" />
                <span className="hidden sm:inline">Show Less</span>
              </>
            ) : (
              <>
                <ChevronDown className="w-3 h-3 sm:w-4 sm:h-4" />
                <span className="hidden sm:inline">Show Details</span>
              </>
            )}
          </button>
        </div>
      </div>

      {isExpanded && (
        <div className="p-3 sm:p-5" style={{ backgroundColor: "#10182b" }}>
          {analysis.summary && (
            <div className="mb-5 rounded-xl p-3 sm:p-4" style={{ backgroundColor: "#1c2436" }}>
              <p className="text-gray-200 text-xs sm:text-sm leading-relaxed">{analysis.summary}</p>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-5">
            <div className="rounded-xl p-3 sm:p-4 transition-all duration-300 hover:shadow-lg hover:shadow-primary-cyan/10 hover:scale-[1.01]" style={{ backgroundColor: "#1c2436" }}>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-9 h-9 rounded-xl bg-primary-cyan/20 flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-primary-cyan" />
                </div>
                <span className="text-primary-cyan text-sm font-bold uppercase tracking-wide">Month over Month</span>
              </div>
              <div className="space-y-3">
                <div className="flex justify-between items-center flex-wrap gap-2">
                  <span className="text-gray-400 text-sm">{analysis.previousMonth || "Previous Month"}</span>
                  <span className="text-white font-semibold">{formatCurrency(analysis.previousMonthTotal)}</span>
                </div>
                <div className="flex justify-between items-center flex-wrap gap-2">
                  <span className="text-gray-400 text-sm">{analysis.currentMonth || "Current Month"}</span>
                  <span className="text-white font-semibold">{formatCurrency(analysis.currentMonthTotal)}</span>
                </div>
                <div className="flex justify-between items-center flex-wrap gap-2 pt-2 border-t border-gray-700">
                  <span className="text-gray-400 text-sm">Change</span>
                  <div className="flex items-center gap-1">
                    {getTrendIcon(analysis.monthOverMonthChange)}
                    <span className={`font-semibold ${analysis.monthOverMonthChange > 0 ? "text-red-400" : "text-green-400"}`}>
                      {formatPercent(analysis.monthOverMonthChange)}
                    </span>
                  </div>
                </div>
                {analysis.mainDriverService && (
                  <div className="pt-1">
                    <p className="text-gray-500 text-xs break-words">Main Driver: {analysis.mainDriverService}</p>
                  </div>
                )}
              </div>
            </div>

            <div className="rounded-xl p-3 sm:p-4 transition-all duration-300 hover:shadow-lg hover:shadow-primary-cyan/10 hover:scale-[1.01]" style={{ backgroundColor: "#1c2436" }}>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-9 h-9 rounded-xl bg-primary-cyan/20 flex items-center justify-center">
                  <Calendar className="w-5 h-5 text-primary-cyan" />
                </div>
                <span className="text-primary-cyan text-sm font-bold uppercase tracking-wide">Service Breakdown</span>
              </div>
              <div className="space-y-2">
                <div className="flex justify-between items-center flex-wrap gap-2">
                  <span className="text-gray-400 text-sm">Top Driver</span>
                  <span className="text-white font-semibold break-words text-right">{analysis.topDriver || "N/A"}</span>
                </div>
                <div className="flex justify-between items-center flex-wrap gap-2">
                  <span className="text-gray-400 text-sm">Fastest Growing</span>
                  <span className="text-yellow-400 font-semibold break-words text-right">{analysis.fastestGrowing || "N/A"}</span>
                </div>
                {analysis.serviceBreakdownJson && (
                  <div className="pt-2 border-t border-gray-700">
                    <details className="text-xs">
                      <summary className="text-gray-500 cursor-pointer hover:text-gray-400">View all services</summary>
                      <div className="mt-2 space-y-1">
                        {(() => {
                          try {
                            const services = JSON.parse(analysis.serviceBreakdownJson);
                            return services.map((service, idx) => (
                              <div key={idx} className="flex justify-between items-center flex-wrap gap-2">
                                <span className="text-gray-500 text-xs break-words">{service.serviceName}</span>
                                <span className="text-gray-300">{formatCurrency(service.averageCost)}</span>
                              </div>
                            ));
                          } catch (e) {
                            return <p className="text-gray-500">No data</p>;
                          }
                        })()}
                      </div>
                    </details>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-5">
            <div className="rounded-xl p-3 sm:p-4 transition-all duration-300 hover:shadow-lg hover:shadow-primary-cyan/10 hover:scale-[1.01]" style={{ backgroundColor: "#1c2436" }}>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-9 h-9 rounded-xl bg-primary-cyan/20 flex items-center justify-center">
                  <Zap className="w-5 h-5 text-primary-cyan" />
                </div>
                <span className="text-primary-cyan text-sm font-bold uppercase tracking-wide">Prediction</span>
              </div>
              <div className="space-y-3">
                <div className="flex justify-between items-center flex-wrap gap-2">
                  <span className="text-gray-400 text-sm">Next Month</span>
                  <span className="text-white font-semibold break-words text-right">{analysis.predictedNextMonth || "N/A"}</span>
                </div>
                <div className="flex justify-between items-center flex-wrap gap-2">
                  <span className="text-gray-400 text-sm">Predicted Total</span>
                  <span className="text-white font-bold">{formatCurrency(analysis.predictedTotal)}</span>
                </div>
                <div className="flex justify-between items-center flex-wrap gap-2">
                  <span className="text-gray-400 text-sm">Change</span>
                  <div className="flex items-center gap-1">
                    {getTrendIcon(analysis.predictedChangePercent)}
                    <span className={`font-semibold ${getDirectionColor(analysis.predictedDirection)}`}>
                      {formatPercent(analysis.predictedChangePercent)}
                    </span>
                  </div>
                </div>
                {analysis.predictionConfidence && (
                  <div className="pt-2">
                    <span className={`text-xs px-2 py-1 rounded-full font-semibold ${getConfidenceColor(analysis.predictionConfidence)}`}>
                      Confidence: {analysis.predictionConfidence}
                    </span>
                  </div>
                )}
              </div>
            </div>

            <div className="rounded-xl p-3 sm:p-4 transition-all duration-300 hover:shadow-lg hover:shadow-primary-cyan/10 hover:scale-[1.01]" style={{ backgroundColor: "#1c2436" }}>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-9 h-9 rounded-xl bg-primary-cyan/20 flex items-center justify-center">
                  <Shield className="w-5 h-5 text-primary-cyan" />
                </div>
                <span className="text-primary-cyan text-sm font-bold uppercase tracking-wide">Recommendation</span>
              </div>
              <p className="text-gray-200 text-sm sm:text-base leading-relaxed font-medium break-words">
                {analysis.recommendationSuggestion || "No recommendation available"}
              </p>
              {analysis.recommendationAction && analysis.recommendationAction !== "NONE" && (
                <div className="mt-4">
                  <span className={`text-xs px-2 sm:px-3 py-1.5 rounded-full font-semibold ${getRecommendationActionColor(analysis.recommendationAction)}`}>
                    Action: {analysis.recommendationAction.replace(/_/g, " ")}
                  </span>
                </div>
              )}
            </div>
          </div>

          {analysis.anomaliesJson && (
            <div className="rounded-xl p-3 sm:p-4 transition-all duration-300 hover:shadow-lg hover:shadow-red-500/10 hover:scale-[1.01]" style={{ backgroundColor: "#1c2436" }}>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-9 h-9 rounded-xl bg-red-500/20 flex items-center justify-center">
                  <svg className="w-5 h-5 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
                <span className="text-red-400 text-sm font-bold uppercase tracking-wide">Anomalies</span>
              </div>
              <div>
                {(() => {
                  try {
                    const anomalies = JSON.parse(analysis.anomaliesJson);
                    if (Array.isArray(anomalies) && anomalies.length > 0) {
                      return anomalies.map((anomaly, idx) => (
                        <p key={idx} className="text-gray-300 text-sm mb-2 break-words">• {anomaly}</p>
                      ));
                    }
                    return <p className="text-green-400 text-sm">No anomalies detected</p>;
                  } catch (e) {
                    return <p className="text-gray-500 text-sm">No data</p>;
                  }
                })()}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AICostAnalysisCard;